import React from "react";
import { 
    Table, 
    ScrollArea, 
    Badge, 
    Skeleton,
    Text,
    ActionIcon,
    Group,
    Tooltip,
    Paper,
    Card,
    Stack,
    Flex,
    Box,
    useMantineTheme
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Student } from "../types/type.student";
import { IconEye, IconTrash, IconMail, IconPhone, IconCalendar, IconUser } from "@tabler/icons-react";

interface StudentTableProps {
    students: Student[];
    loading: boolean;
    onViewStudent: (student: Student) => void;
    onDeleteStudent: (studentId: string) => void;
}

export const StudentTable: React.FC<StudentTableProps> = ({
    students,
    loading,
    onViewStudent,
    onDeleteStudent,
}) => {
    const theme = useMantineTheme();
    const isMobile = useMediaQuery("(max-width: 768px)");

    if (loading) {
        return (
            <Paper shadow="xs" p="md" radius="md" withBorder>
                {Array(5).fill(0).map((_, index) => (
                    <Skeleton key={index} height={isMobile ? 180 : 50} mb={10} radius="md" />
                ))}
            </Paper>
        );
    }

    if (students.length === 0) {
        return (
            <Paper shadow="xs" p="xl" radius="md" withBorder>
                <Text ta="center" size="lg" fw={500} c="dimmed">
                    No students found. Try changing your search criteria.
                </Text>
            </Paper>
        );
    }

    // Mobile Card View
    if (isMobile) {
        return (
            <Stack gap="md">
                {students.map((student) => (
                    <Card 
                        key={student.student_id} 
                        shadow="sm" 
                        padding="lg" 
                        radius="md" 
                        withBorder
                        style={{
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = theme.shadows.md;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = theme.shadows.sm;
                        }}
                    >
                        {/* Header with ID and Actions */}
                        <Group justify="space-between" mb="md">
                            <Badge 
                                color="blue" 
                                variant="gradient" 
                                gradient={{ from: 'blue', to: 'cyan' }}
                                size="lg"
                                style={{ fontSize: '14px' }}
                            >
                                #{student.student_id}
                            </Badge>
                            <Group gap="xs">
                                <Tooltip label="View Details" position="top">
                                    <ActionIcon
                                        variant="light"
                                        color="blue"
                                        size="lg"
                                        radius="md"
                                        onClick={() => onViewStudent(student)}
                                    >
                                        <IconEye size={18} />
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Delete Student" position="top">
                                    <ActionIcon
                                        variant="light"
                                        color="red"
                                        size="lg"
                                        radius="md"
                                        onClick={() => {
                                            const confirmed = window.confirm(
                                                `Are you sure you want to delete ${student.name}?`
                                            );
                                            if (confirmed) onDeleteStudent(student.student_id);
                                        }}
                                    >
                                        <IconTrash size={18} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        </Group>

                        {/* Student Name */}
                        <Group gap="xs" mb="md">
                            <IconUser size={20} color={theme.colors.blue[6]} />
                            <Text fw={600} size="lg" style={{ flex: 1 }}>
                                {student.name}
                            </Text>
                        </Group>

                        {/* Student Details */}
                        <Stack gap="sm">
                            <Group gap="xs" wrap="nowrap">
                                <IconMail size={18} color={theme.colors.gray[6]} />
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                    <Text size="xs" c="dimmed" mb={2}>Email</Text>
                                    <Text size="sm" truncate>{student.email}</Text>
                                </Box>
                            </Group>

                            <Group gap="xs" wrap="nowrap">
                                <IconPhone size={18} color={theme.colors.gray[6]} />
                                <Box style={{ flex: 1 }}>
                                    <Text size="xs" c="dimmed" mb={2}>Mobile</Text>
                                    <Text size="sm">{student.mobile}</Text>
                                </Box>
                            </Group>

                            <Group gap="xs" wrap="nowrap">
                                <IconCalendar size={18} color={theme.colors.gray[6]} />
                                <Box style={{ flex: 1 }}>
                                    <Text size="xs" c="dimmed" mb={2}>Enrollment Date</Text>
                                    <Text size="sm">
                                        {new Date(student.enrollDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                    </Text>
                                </Box>
                            </Group>
                        </Stack>
                    </Card>
                ))}
            </Stack>
        );
    }

    // Desktop Table View
    return (
        <Paper 
            shadow="sm" 
            radius="md" 
            withBorder
            style={{ 
                overflow: 'hidden',
                background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)'
            }}
        >
            <ScrollArea>
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Student ID</Table.Th>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Email</Table.Th>
                            <Table.Th>Mobile</Table.Th>
                            <Table.Th style={{ width: "120px" }}>Actions</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {students.map((student) => (
                            <Table.Tr key={student.student_id}>
                                <Table.Td>
                                    <Badge color="blue" variant="light" size="lg">
                                        {student.student_id}
                                    </Badge>
                                </Table.Td>
                                <Table.Td>
                                    <Text fw={500}>{student.name}</Text>
                                </Table.Td>
                                <Table.Td>{student.email}</Table.Td>
                                <Table.Td>{student.mobile}</Table.Td>
                                <Table.Td>
                                    <Group gap={5}>
                                        <Tooltip label="View/Edit Details">
                                            <ActionIcon 
                                                variant="light" 
                                                color="blue" 
                                                onClick={() => onViewStudent(student)}
                                            >
                                                <IconEye size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label="Delete Student">
                                            <ActionIcon 
                                                variant="light" 
                                                color="red" 
                                                onClick={() => {
                                                    const confirmed = window.confirm(
                                                        `Are you sure you want to delete ${student.name}?`
                                                    );
                                                    if (confirmed) onDeleteStudent(student.student_id);
                                                }}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
        </Paper>
    );
};
